import { supabase } from '@/integrations/supabase/client';

/**
 * Verifica se usuário tem permissão para ver todas as filas de trabalho
 * ✅ MASTER, DIRETOR, GERENTE, BKO, QUALIDADE veem tudo
 * (BKO/QUALIDADE podem ter restrição por equipe via bko_equipe_responsavel)
 */
export function canViewAllQueues(userRole: string): boolean {
  return ['master', 'diretor', 'gerente', 'bko', 'qualidade'].includes(userRole);
}

/**
 * ⛔ O BKO NUNCA VÊ EQUIPE QUE NÃO É DELE (Douglas 29/09/2026): "cada BKO
 * contrato tem a sua equipe e a visão dela." A fila é o quadro da função
 * DENTRO das equipes de bko_equipe_responsavel (sem mapa = tudo). O que saiu
 * em 28/09 foi só o recorte por dono (owner_qualidade_id), nunca a equipe.
 * A mesma régua vive no banco em fn_conversas_das_minhas_filas (Inbox).
 */
export async function equipesDoBko(
  profile: { id?: string | null; role?: string | null } | null | undefined,
): Promise<string[] | null> {
  const role = String(profile?.role || '').toLowerCase();
  if (!profile?.id || !['bko', 'qualidade'].includes(role)) return null;
  const ids = await getBkoTeamIds(profile.id);
  return ids.length > 0 ? ids : null;
}

/**
 * ⛔ CADA BKO VÊ SÓ OS PEDIDOS DELE NAS FILAS DO CONTRATO (Douglas 09/10/2026):
 * "se cada um tem o seu, por que olham e participam dos pedidos e travam por
 * outros?" Na Criação, Input, Pendência Input e Aguardando HP o pedido é de
 * quem o pegou para criar (bko_criacao_id = v_planilha_mae.bko_id). A tela do
 * Input liberava só o 1º da fila, de quem fosse, e o BKO adiava o pedido do
 * colega para chegar no seu (101 adiamentos em outubro). Quem tem função de
 * BKO vê os seus e os sem dono; a supervisora do Backoffice e a gestão veem
 * a fila inteira.
 */
export function bkoVeSoOsSeus(profile: { funcao?: string | null } | null | undefined): boolean {
  return String(profile?.funcao || '').toUpperCase().startsWith('BKO_');
}

export function pedidoEhMeuOuLivre(dono: string | null | undefined, meuId: string | null | undefined): boolean {
  return !dono || (!!meuId && dono === meuId);
}

/**
 * Verifica se usuário pode ver dados da equipe (não de todos)
 * Supervisor e Coordenador veem apenas dados das suas equipes
 */
export function canViewTeamData(userRole: string): boolean {
  return ['supervisor', 'coordenador'].includes(userRole);
}

/**
 * Busca IDs das equipes mapeadas para um BKO/Qualidade
 * - Se retornar array vazio = "Coringa" (acesso global)
 * - Se retornar equipes = filtrar apenas por essas equipes
 */
export async function getBkoTeamIds(userId: string): Promise<string[]> {
  const { data } = await supabase
    .from('bko_equipe_responsavel')
    .select('equipe_id')
    .eq('usuario_id', userId);
  return data?.map(r => r.equipe_id) || [];
}

/**
 * Busca IDs das equipes que o usuário pode acessar baseado no role
 */
export async function getUserAccessibleTeamIds(userId: string, userRole: string): Promise<string[]> {
  // Gestores de alto nível veem todas as equipes (filtro vazio = sem restrição)
  if (['master', 'diretor', 'gerente'].includes(userRole)) {
    return [];
  }
  
  // Coordenadores: equipes que lideram
  if (userRole === 'coordenador') {
    const { data } = await supabase
      .from('equipes')
      .select('id')
      .eq('lider_id', userId);
    return data?.map(e => e.id) || [];
  }
  
  // Supervisores e Consultores: suas próprias equipes
  const { data } = await supabase
    .from('equipe_membro')
    .select('equipe_id')
    .eq('usuario_id', userId);
  
  return data?.map(em => em.equipe_id) || [];
}

/**
 * Busca IDs das filas do usuário
 */
export async function getUserQueueIds(userId: string): Promise<string[]> {
  const { data } = await supabase
    .from('usuario_fila')
    .select('fila_id')
    .eq('usuario_id', userId);

  return data?.map(uf => uf.fila_id) || [];
}

/**
 * Aplica filtro de fila baseado em permissões e fila ativa
 * ✅ Hierarquia correta por role
 */
export async function applyQueueFilter(
  query: any,
  userId: string,
  userRole: string,
  filaAtivaId?: string | null
): Promise<any> {
  // ✅ Gestor de alto nível vê tudo
  if (canViewAllQueues(userRole)) {
    // Se tem fila ativa selecionada (e não é "TODAS"), filtrar por ela
    if (filaAtivaId && filaAtivaId !== 'TODAS') {
      return query.eq('fila_id', filaAtivaId);
    }
    return query;
  }

  // ✅ Buscar equipes acessíveis para Coordenadores e Supervisores
  const accessibleTeamIds = await getUserAccessibleTeamIds(userId, userRole);
  
  // ✅ Consultor: buscar filas do usuário
  const queueIds = await getUserQueueIds(userId);

  if (queueIds.length === 0 && accessibleTeamIds.length === 0) {
    // Usuário sem filas e sem equipes: ver apenas itens sem fila OU seus próprios
    return query.or(`fila_id.is.null,consultor_id.eq.${userId}`);
  }

  // ✅ Se tem fila ativa selecionada
  if (filaAtivaId && filaAtivaId !== 'TODAS') {
    if (queueIds.includes(filaAtivaId)) {
      // Fila pertence ao usuário: mostrar todos os itens dela
      return query.eq('fila_id', filaAtivaId);
    } else if (accessibleTeamIds.length > 0) {
      // Supervisor/Coordenador: filtrar por equipes acessíveis
      return query.or(`fila_id.eq.${filaAtivaId},consultor_id.eq.${userId}`);
    } else {
      // Não tem acesso: mostrar apenas seus próprios itens
      return query.eq('consultor_id', userId);
    }
  }

  // ✅ Sem fila ativa: construir filtro baseado em role
  const conditions: string[] = [];
  
  // Sempre pode ver itens sem fila
  conditions.push('fila_id.is.null');
  
  // Sempre pode ver seus próprios itens
  conditions.push(`consultor_id.eq.${userId}`);
  
  // Se tem filas, pode ver itens dessas filas
  if (queueIds.length > 0) {
    conditions.push(`fila_id.in.(${queueIds.join(',')})`);
  }
  
  return query.or(conditions.join(','));
}
